import { cn } from '@rental/ui'
import { type ReactNode, useId } from 'react'

type Option<T extends string> = { value: T; label: ReactNode }

/** One of a few: the browser's radios. */
type OneOf<T extends string> = {
  multiple?: false
  /** Nothing chosen yet: null. */
  value: T | null
  onChange: (value: T) => void
}

/**
 * Any of a few (W2-S S1: Prezzi's weekdays, its categories): the browser's
 * checkboxes, in a group named by its label. The same look, the same 44 px.
 */
type AnyOf<T extends string> = {
  multiple: true
  /** What is chosen, none to all. */
  value: readonly T[]
  /** What is chosen after a press, in the options' own order. */
  onChange: (value: T[]) => void
}

/**
 * The choices after one was pressed in a `multiple` group: it is added, or
 * taken away, and what is chosen keeps the options' order, whatever the
 * order of the presses.
 */
export function toggleChoice<T extends string>(
  options: readonly { value: T }[],
  chosen: readonly T[],
  pressed: T,
): T[] {
  const next = chosen.includes(pressed)
    ? chosen.filter((value) => value !== pressed)
    : [...chosen, pressed]
  return options.map((option) => option.value).filter((value) => next.includes(value))
}

/**
 * A choice of a few, all in sight (DESIGN.md, "A screen and its parts"): the
 * browser's own radios, each inside its words as a chip a finger can press,
 * in a radio group named by its label. One drawing for every form that asks
 * one of a few: the booking's channel, a payment's method, a deposit's.
 * `multiple` asks any of a few, with checkboxes in a group, drawn the same.
 * Where a heading over it already says what is asked, the label is said, not
 * shown (`labelHidden`): the same word twice in a row reads as two questions.
 */
export function ChoiceChips<T extends string>({
  name,
  label,
  labelHidden = false,
  options,
  className,
  ...choice
}: {
  /** The inputs' name: unique on the page, as two groups of one name are one group. */
  name: string
  label: string
  labelHidden?: boolean
  options: readonly Option<T>[]
  className?: string
} & (OneOf<T> | AnyOf<T>)) {
  const labelId = useId()
  const isChosen = (value: T) =>
    choice.multiple ? choice.value.includes(value) : choice.value === value
  const press = (value: T) => {
    if (choice.multiple) choice.onChange(toggleChoice(options, choice.value, value))
    else choice.onChange(value)
  }
  return (
    <div
      role={choice.multiple ? 'group' : 'radiogroup'}
      {...(labelHidden ? { 'aria-label': label } : { 'aria-labelledby': labelId })}
      className={cn('flex flex-col gap-2', className)}
    >
      {labelHidden ? null : (
        <span id={labelId} className="text-sm font-medium text-text">
          {label}
        </span>
      )}
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <label
            key={option.value}
            className={cn(
              'flex min-h-touch cursor-pointer items-center gap-2 rounded-sm border px-4',
              isChosen(option.value)
                ? 'border-brand bg-surface font-bold text-brand'
                : 'border-text-muted',
            )}
          >
            <input
              type={choice.multiple ? 'checkbox' : 'radio'}
              name={name}
              value={option.value}
              checked={isChosen(option.value)}
              onChange={() => press(option.value)}
              className="accent-brand"
            />
            {option.label}
          </label>
        ))}
      </div>
    </div>
  )
}
